"use client";

import Image from '@/components/ui/Image';
import Link from "next/link";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import {
  NavigationMenu,
  NavigationMenuList,
} from "@/components/ui/navigation-menu";
import { Accordion } from "@/components/ui/accordion";
import { PageContext } from '@/components/builder';
import { useContext, useEffect, useMemo } from 'react';
import { useIsPreviewing } from '@builder.io/react';
import { useHeaderSheet } from '@/lib/header-sheet-context';

import { MenuItem } from "./components/MenuItem";
import { MobileMenuItem } from "./components/MobileMenuItem";
import { MenuItemType, AccountAreaType } from "@/types/menu";
import { ButtonStyleType, sectionVariants } from "@/types";
import { cn, hoverBackgroundColors, sectionBackgroundColors } from "@/lib/utils";
import { AnnouncementBar } from "./components/AnnouncementBar";
import Sticky from 'react-sticky-el';
import parse from "html-react-parser";
import { ShimmerButton } from "../magicui/shimmer-button";
import { useState } from "react";

// Define the shape of header props
type HeaderPropsType = {
  darkMode?: boolean;
  showNav?: boolean;
  variant?: sectionVariants;
  menuItem?: MenuItemType[];
  accountArea?: AccountAreaType;
  showAnnouncementBar?: boolean;
  announcementBarDismissable?: boolean;
  announcementBarTitle?: string;
  announcementBarContent?: string;
  announcementBarHasButton?: boolean;
  announcementBarButtonText?: string;
  announcementBarButtonLink?: string;
  announcementBarButtonStyle?: ButtonStyleType;
  customAccountArea?: boolean;
  withPartnerLogo?: boolean;
  partnerLogo?: string;
  customText?: string;
  customButtonText?: string;
  customButtonLink?: string;
  customButtonStyle?: ButtonStyleType;
};

interface HeaderProps extends HeaderPropsType {
  attributes?: React.HTMLProps<HTMLElement>;
}

// Default values for header props
const DEFAULT_PROPS: Required<Omit<HeaderPropsType, 'menuItem'>> = {
  darkMode: false,
  showNav: true,
  variant: "A",
  showAnnouncementBar: false,
  announcementBarDismissable: true,
  announcementBarTitle: "",
  announcementBarContent: "",
  announcementBarHasButton: false,
  announcementBarButtonText: "",
  announcementBarButtonLink: "",
  announcementBarButtonStyle: "primary",
  accountArea: {
    loginButton: true,
    loginLink: "https://app.octet.com/",
    loginText: "Log in",
    enquiryButton: true,
    enquiryLink: "/enquire",
    enquiryText: "Enquire now",
  },
  customAccountArea: false,
  withPartnerLogo: false,
  partnerLogo: "",
  customText: "",
  customButtonText: "",
  customButtonLink: "",
  customButtonStyle: "primary",
};

// Utility function to resolve props based on preview mode
function resolveProps(
  contextProps: Partial<HeaderPropsType> | undefined,
  directProps: Partial<HeaderPropsType>,
  isPreviewing: boolean
): Required<Omit<HeaderPropsType, 'menuItem'>> & { menuItem?: MenuItemType[] } {
  // Create a type-safe result object
  const result = {
    darkMode: false,
    showNav: true,
    variant: "A" as sectionVariants,
    showAnnouncementBar: false,
    announcementBarDismissable: true,
    announcementBarTitle: "",
    announcementBarContent: "",
    announcementBarHasButton: false,
    announcementBarButtonText: "",
    announcementBarButtonLink: "",
    announcementBarButtonStyle: "primary" as ButtonStyleType,
    accountArea: {
      loginButton: true,
      loginLink: "https://app.octet.com/",
      loginText: "Log in",
      enquiryButton: true,
      enquiryLink: "/enquire",
      enquiryText: "Enquire now",
    } as AccountAreaType,
    customAccountArea: false,
    withPartnerLogo: false,
    partnerLogo: "",
    customText: "",
    customButtonText: "",
    customButtonLink: "",
    customButtonStyle: "primary" as ButtonStyleType,
  } as Required<Omit<HeaderPropsType, 'menuItem'>>;
  
  // Helper to get value based on preview mode
  function getValue<T extends string | boolean | AccountAreaType[keyof AccountAreaType] | MenuItemType[]>(
    contextValue: T | undefined,
    directValue: T | undefined,
    defaultValue: T
  ): T {
    return isPreviewing 
      ? (directValue ?? contextValue ?? defaultValue)
      : (contextValue ?? directValue ?? defaultValue);
  }

  // Handle simple props
  Object.entries(DEFAULT_PROPS).forEach(([key, defaultValue]) => {
    if (key !== 'accountArea') {
      const typedKey = key as keyof Omit<HeaderPropsType, 'accountArea' | 'menuItem'>;
      type ValueType = typeof DEFAULT_PROPS[typeof typedKey];
      (result as Record<typeof typedKey, ValueType>)[typedKey] = getValue<ValueType>(
        contextProps?.[typedKey],
        directProps[typedKey],
        defaultValue as ValueType
      );
    }
  });

  // Handle nested accountArea object
  Object.entries(DEFAULT_PROPS.accountArea).forEach(([key, defaultValue]) => {
    const typedKey = key as keyof AccountAreaType;
    type ValueType = AccountAreaType[typeof typedKey];
    (result.accountArea as Record<typeof typedKey, ValueType>)[typedKey] = getValue<ValueType>(
      contextProps?.accountArea?.[typedKey],
      directProps.accountArea?.[typedKey],
      defaultValue
    );
  });

  // Handle menuItem separately since it's optional
  const menuItems = getValue<MenuItemType[] | undefined>(
    contextProps?.menuItem,
    directProps.menuItem,
    undefined
  );

  return {
    ...result,
    menuItem: menuItems
  };
}

export function Header(props: HeaderProps) {
  const { data } = useContext(PageContext);
  const contextProps = (data as unknown as { headerProps?: Partial<HeaderPropsType> })?.headerProps || ({} as Partial<HeaderPropsType>);
  const isPreviewing = useIsPreviewing();
  const [hasMounted, setHasMounted] = useState(false);
  const { isSheetOpen, closeSheet, openSheet, setCustomAccountArea, setAccountArea } = useHeaderSheet();

  // Avoid hydration mismatch: preview detection can differ between SSR and browser (cookies/query params)
  useEffect(() => {
    setHasMounted(true);
  }, []);

  // Get effective values for each prop
  const effectiveProps = resolveProps(contextProps, props, hasMounted ? isPreviewing : false);

  const {
    darkMode,
    showNav,
    variant,
    menuItem,
    showAnnouncementBar,
    announcementBarDismissable,
    announcementBarTitle,
    announcementBarContent,
    announcementBarHasButton,
    announcementBarButtonText,
    announcementBarButtonLink,
    announcementBarButtonStyle,
    accountArea,
    customAccountArea,
    withPartnerLogo,
    partnerLogo,
    customText,
    customButtonText,
    customButtonLink,
    customButtonStyle,
  } = effectiveProps;

  // Memoize stringified accountArea to avoid infinite loop from object reference changes
  const accountAreaString = useMemo(() => JSON.stringify(accountArea), [accountArea]);

   // Set account area data in context
   useEffect(() => {
    setAccountArea(accountArea);
    // We use accountAreaString instead of accountArea to prevent infinite loops
    // from object reference changes while still detecting value changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setAccountArea, accountAreaString]);

  // Set custom account area data in context
  useEffect(() => {
    setCustomAccountArea({
      customAccountArea,
      withPartnerLogo,
      partnerLogo,
      customText,
      customButtonText,
      customButtonLink,
      customButtonStyle,
    });
  }, [customAccountArea, withPartnerLogo, partnerLogo, customText, customButtonText, customButtonLink, customButtonStyle, setCustomAccountArea]);

  const logo = {
    url: "/",
    src: "/logo.svg",
    darkSrc: "/logo-white.svg",
    alt: "Octet",
    title: "Octet",
  };

  const { key } = props.attributes ?? {};

  const sectionClass = cn(
    darkMode ? "dark" : "light",
    "header-section",
    "m-0 p-0 overscroll-contain z-100 w-full transition-shadow duration-320 relative", 
   
    sectionBackgroundColors(darkMode, variant)
  );

  const hoverBgName = hoverBackgroundColors(darkMode, variant);

  const stickySectionClass = cn(
    "group-[.page-nav-sticky]/body:shadow-none",
    "group-[.page-nav-sticky]/body:hidden",
  );

  // Add CSS variables for logo URLs
  const logoStyles = {
    '--logo-light-url': `url(${logo.src})`,
    '--logo-dark-url': `url(${logo.darkSrc})`,
  } as React.CSSProperties;

  return (
    <>
      <AnnouncementBar
        showAnnouncementBar={showAnnouncementBar}
        announcementBar={{
          dismissable: announcementBarDismissable,
          title: announcementBarTitle,
          content: announcementBarContent,
          hasButton: announcementBarHasButton,
          buttonLink: announcementBarButtonLink,
          buttonText: announcementBarButtonText,
          buttonStyle: announcementBarButtonStyle,
        }}
        id="announcement-bar"
      />
      <Sticky
        key={key}
        wrapperClassName={sectionClass}
        stickyClassName={cn(stickySectionClass, "shadow-md")}
      >
        <div 
          className={cn(
            darkMode ? "dark" : "light",
            "container py-4 text-foreground relative z-50"
          )}
        >
          {/* Desktop Navigation */}
          <nav className="hidden relative justify-between xl:flex gap-10 2xl:gap-10">
            <div className="flex items-center gap-4">
              {/* Logo */}
              {!withPartnerLogo && showNav && (
                <Link href={logo.url} className="w-[90px]">
                  <Image
                    src={logo.src}
                    data-src-dark={logo.darkSrc}
                    data-theme-logo
                    alt={logo.alt}
                    width={90}
                    height={30}
                    loading="eager"
                    priority
                    style={logoStyles}
                    className="theme-logo"
                  />
                </Link>
              )}
              {!withPartnerLogo && !showNav && (
                <div className="w-[90px]">
                  <Image
                    src={logo.src}
                    data-src-dark={logo.darkSrc}
                    data-theme-logo
                    alt={logo.alt}
                    width={90}
                    height={30}
                    loading="eager"
                    priority
                    style={logoStyles}
                    className="theme-logo"
                  />
                </div>
              )}
              {withPartnerLogo && (
                <Image src={partnerLogo} alt={partnerLogo} width={0} height={30} className="w-auto" />
              )}
            </div>
            <div className="flex items-center flex-1">
              {showNav && (
                <NavigationMenu>
                  <NavigationMenuList>
                    {menuItem?.map((item: MenuItemType) => (
                      <MenuItem key={item.label} item={item} hoverBgName={hoverBgName} />
                    ))}
                  </NavigationMenuList>
                </NavigationMenu>
              )}
            </div>
            
            {customAccountArea ? (
              <div className="flex gap-2">
                {customText && <span className="flex items-center paragraph-base mr-2 text-foreground">{parse(customText)}</span>}
                {customButtonText && (
                  <Button asChild variant={customButtonStyle} size="sm" className="z-10">
                    <Link href={customButtonLink || "/"}>{customButtonText}</Link>
                  </Button>
                )}
              </div>
            ) : (
              <div className="flex gap-2 2xl:w-60 justify-end items-center">
                {accountArea.loginButton && (
                  <Button asChild variant="ghost" size="sm">
                    <Link className="paragraph-sm font-normal flex items-center gap-2 z-10" href={accountArea.loginLink || "/"}>
                      <span className="material-symbols-outlined">login</span>
                      {accountArea.loginText}
                    </Link>
                  </Button>
                )}
                {accountArea.enquiryButton && (
                  <ShimmerButton>
                    <Link className="paragraph-sm font-bold text-nowrap z-10" href={accountArea.enquiryLink || "/"}>{accountArea.enquiryText}</Link>
                  </ShimmerButton>
                )}
              </div>
            )}
          </nav>

          {/* Mobile Navigation */}
          <div className="block xl:hidden">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-4 min-w-0 flex-shrink">
                {!withPartnerLogo && showNav && (
                    <Link href={logo.url} className="max-w-[90px] w-full flex-shrink">
                      <Image
                        src={logo.src}
                        data-src-dark={logo.darkSrc}
                        data-theme-logo
                        alt={logo.alt}
                        width={90}
                        height={30}
                        loading="eager"
                        priority
                        style={logoStyles}
                        className="theme-logo w-full h-auto"
                      />
                    </Link>
                  )}
                  {!withPartnerLogo && !showNav && (
                    <div className="max-w-[90px] w-full flex-shrink">
                      <Image
                        src={logo.src}
                        data-src-dark={logo.darkSrc}
                        data-theme-logo
                        alt={logo.alt}
                        width={90}
                        height={30}
                        loading="eager"
                        priority
                        style={logoStyles}
                        className="theme-logo w-full h-auto"
                      />
                    </div>
                  )}
                {withPartnerLogo && (
                    <div className="max-h-[30px] w-full flex-shrink">
                      <Image src={partnerLogo} alt={partnerLogo} width={0} height={30} className="w-full h-auto object-contain" />
                    </div>
                )}
              </div>
              <Sheet open={isSheetOpen} onOpenChange={closeSheet}>
                {!customAccountArea ? (
                  <div className="flex items-center gap-2">
                    {accountArea.loginButton && (
                      <Button asChild variant="ghost" size="sm">
                        <Link className="font-normal flex items-center gap-2 z-10" href={accountArea.loginLink || "/"}>
                          <span className="material-symbols-outlined">login</span>
                          {accountArea.loginText}
                        </Link>
                      </Button>
                    )}
                    <Button variant="ghost" size="icon" onClick={openSheet} className="cursor-pointer z-10">
                      <span className="material-symbols-outlined text-primary">menu</span>
                    </Button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    {customText && <span className="items-center hidden md:flex paragraph-base mr-2 text-foreground">{parse(customText)}</span>}
                    {customButtonText && (
                      <Button asChild variant={customButtonStyle} size="sm">
                        <Link href={customButtonLink || "/"}>{customButtonText}</Link>
                      </Button>
                    )} 
                  </div>
                )}
                <SheetContent className={cn(
                  darkMode ? 'dark' : 'light',
                  darkMode ? 'bg-muted' : 'bg-background',
                  "w-full max-w-full sm:w-[400px] sm:max-w-[400px] h-full flex flex-col justify-between text-foreground overflow-y-auto border-none z-500"
                )}>
                  <SheetHeader className="sr-only">
                    <SheetTitle>Menu</SheetTitle>
                  </SheetHeader>
                  <div className="relative flex flex-col flex-1 gap-6">
                    <div className="flex gap-10 p-4 justify-between items-center sticky top-0 left-0 right-0 bg-background dark:bg-muted z-10">
                      <Link href={logo.url} className="w-[90px]">
                        <Image
                          src={logo.src}
                          data-src-dark={logo.darkSrc}
                          data-theme-logo
                          alt={logo.alt}
                          width={90}
                          height={30}
                          loading="eager"
                          priority
                          style={logoStyles}
                          className="theme-logo"
                        />
                      </Link>
                      <Button variant="ghost" size="icon" onClick={closeSheet}>
                        <span className="material-symbols-outlined text-primary">close</span>
                      </Button>
                    </div>
                    {showNav && (
                      <div className="flex flex-col flex-1">
                        <Accordion
                          type="single"
                          collapsible
                          className="flex w-full flex-col border-y"
                        >
                          {menuItem?.map((item: MenuItemType) => (
                            <MobileMenuItem key={item.label} item={item} />
                          ))}
                        </Accordion>
                      </div>
                    )}
                    <div className="flex flex-col gap-3 p-4">
                      {customAccountArea ? (
                        <>
                          {customText && <span>{parse(customText)}</span>}
                          {customButtonText && (
                            <Button asChild variant={customButtonStyle} size="sm">
                              <Link href={customButtonLink || "/"}>{customButtonText}</Link>
                            </Button>
                          )}
                        </>
                      ) : (
                        <>
                          {accountArea.enquiryButton && (
                            <Button asChild variant="primary" size="lg">
                              <Link href={accountArea.enquiryLink || "/"}>{accountArea.enquiryText}</Link>
                            </Button>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>
        <div className="group-[.page-nav-sticky]/body:h-[72px]"></div>
      </Sticky>

      
      
      {/* Full-height overlay when dropdown is open */}
      <div 
        className={cn(
          "fixed inset-0 -z-1 w-full h-full bg-transparent transition-opacity !duration-300 pointer-events-none opacity-0",
          "[html:has([data-slot=navigation-menu-viewport][data-state=open])_&]:bg-black/30",
          "[html:has([data-slot=navigation-menu-viewport][data-state=open])_&]:opacity-100 [html:has([data-slot=navigation-menu-viewport][data-state=open])_&]:z-91",
          "[html:has([data-slot=navigation-menu-viewport][data-state=closed])_&]:opacity-0",
          "[html:has([data-slot=navigation-menu-viewport][data-state=open])_&]:pointer-events-auto",
          "group-[.page-nav-sticky]/body:!hidden group-[.page-nav-sticky]/body:!z-0",
        )}
        data-slot="navigation-overlay"
      />
      
      <div
        className={cn(
          darkMode ? "dark" : "light",
          sectionBackgroundColors(darkMode, variant)
        )}
        style={{ 
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          zIndex: '-5',
          pointerEvents: 'none',
        }}
      />
    </>
  );
}
